import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { BiFilter } from "react-icons/bi";
import { GrPowerReset } from "react-icons/gr";
import moment from "moment";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";

import "./../Rewards/With.css";
import { fetchDummyWallets } from "../../Actions/adminActions";
import PaginationComponent from "../../Component/Pagination/PaginationControls";
import apiCall from "../../API/API"; // Ensure this path is correct

const DummyUserWallet = () => {
  const { dummyWallets } = useSelector((state) => state.admin);
  const dispatch = useDispatch();

  // --- STATE FOR TABLE & FILTERS ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("none");
  const [search, setSearch] = useState({
    username: "",
    search: "",
    startDate: "",
    endDate: "",
    status: "1",
    uid: "",
  });
  const [limitFilter, setLimitFilter] = useState(20);
  const [dateFilter, setDateFilter] = useState("default");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // --- STATE FOR UPDATE MODAL ---
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [editableWallets, setEditableWallets] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // --- Wallets to exclude from display ---
  const excludedWallets = ['Total Withdrawal', 'Total Payout'];

  const fetchDummyWallet = useCallback(
    debounce((params) => {
      dispatch(fetchDummyWallets(params));
    }, 500),
    [dispatch]
  );

  // --- FILTER & PAGINATION HANDLERS ---
  const toggleFilter = () => setFilter(filter === "none" ? "flex" : "none");
  const handlePagination = (page) => setCurrentPage(page);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "startDate") {
      setStartDate(value);
    } else if (name === "endDate") {
      setEndDate(value);
    } else {
      setSearch((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLimitChange = (e) => {
    setLimitFilter(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when limit changes
  };

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    setDateFilter(value);
    const today = moment();
    let start = "", end = "";
    switch (value) {
      case "today":
        start = today.startOf("day").format("YYYY-MM-DD");
        end = today.endOf("day").format("YYYY-MM-DD");
        break;
      case "yesterday":
        const yesterday = moment().subtract(1, 'days');
        start = yesterday.startOf("day").format("YYYY-MM-DD");
        end = yesterday.endOf("day").format("YYYY-MM-DD");
        break;
      case "this_week":
        start = today.startOf("week").format("YYYY-MM-DD");
        end = today.endOf("week").format("YYYY-MM-DD");
        break;
      case "this_month":
        start = today.startOf("month").format("YYYY-MM-DD");
        end = today.endOf("month").format("YYYY-MM-DD");
        break;
      default:
        break;
    }
    setStartDate(start);
    setEndDate(end);
  };

  const addFilter = () => {
    setCurrentPage(1);
    const queryParams = {
      page: 1,
      limit: limitFilter,
      ...Object.fromEntries(Object.entries(search).filter(([_, v]) => v)),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    };
    dispatch(fetchDummyWallets(queryParams));
  };

  const resetFilter = () => {
    setSearch({ username: "", search: "", startDate: "", endDate: "", status: "1", uid: "" });
    setLimitFilter(20);
    setStartDate("");
    setEndDate("");
    setDateFilter("default");
    setCurrentPage(1);
    dispatch(fetchDummyWallets({ page: 1, limit: 20, status: "1" }));
  };

  // --- UPDATE MODAL HANDLERS ---
  const handleUpdateModalShow = (user) => {
    setSelectedUserForEdit(user);
    // Create a simple key-value object for easy editing: { wallet_slug: value, ... }
    const walletsToEdit = user.wallets.reduce((acc, wallet) => {
      acc[wallet.slug] = wallet.value;
      return acc;
    }, {});
    setEditableWallets(walletsToEdit);
    setShowUpdateModal(true);
  };

  const handleUpdateModalClose = () => {
    setShowUpdateModal(false);
    setSelectedUserForEdit(null);
    setEditableWallets({});
    setIsUpdating(false);
  };

  const handleWalletValueChange = (slug, value) => {
    setEditableWallets(prev => ({ ...prev, [slug]: value }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    const updatePromises = [];
    // Find which wallets have changed and create an API call promise for each
    selectedUserForEdit.wallets.forEach(originalWallet => {
      const originalValue = originalWallet.value;
      const updatedValue = editableWallets[originalWallet.slug];

      // Check if value has changed
      if (String(originalValue) !== String(updatedValue)) {
        const payload = {
          uid: selectedUserForEdit.uid,
          slug: originalWallet.slug,
          value: updatedValue,
        };
        updatePromises.push(apiCall("POST", "/update_fake_user_wallet", payload));
      }
    });

    if (updatePromises.length === 0) {
      toast.info("No changes were made.");
      handleUpdateModalClose();
      return;
    }

    try {
      await Promise.all(updatePromises);
      toast.success("Wallets updated successfully!");
      handleUpdateModalClose();
      // Refresh the table data to show the latest values
      dispatch(fetchDummyWallets({
        page: currentPage,
        limit: limitFilter,
        ...Object.fromEntries(Object.entries(search).filter(([_, v]) => v)),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred while updating.");
      setIsUpdating(false);
    }
  };


  // --- DATA FETCHING & EFFECT HOOKS ---
  useEffect(() => {
    const queryParams = {
      page: currentPage,
      limit: limitFilter,
      ...Object.fromEntries(Object.entries(search).filter(([_, v]) => v)),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    };
    fetchDummyWallet(queryParams);
  }, [dispatch, currentPage, limitFilter, search, startDate, endDate, fetchDummyWallet]);

  useEffect(() => {
    if (dummyWallets?.totalPages) {
      setTotalPages(dummyWallets.totalPages);
    }
  }, [dummyWallets]);

  return (
    <React.Fragment>
      <Container>
        {/* --- FILTER UI --- */}
        <Row id="filterRow">
          <span id="filterLogo1" onClick={toggleFilter}>
            <i><BiFilter /></i> Filter
          </span>
          <Row className="p-0 m-0" style={{ display: filter }}>
            <Col lg="2" md="4"><input type="text" name="username" placeholder="Search By Username" value={search.username} onChange={handleChange} /></Col>
            <Col lg="2" md="4"><input type="text" placeholder="Search User By Uid" name="uid" value={search.uid} onChange={handleChange} /></Col>
            <Col lg="2" md="4"><input type="date" name="startDate" value={startDate} onChange={handleChange} /></Col>
            <Col lg="2" md="4"><input type="date" name="endDate" value={endDate} onChange={handleChange} /></Col>
            <Col lg="2" md="4"><select name="dateFilter" value={dateFilter} onChange={handleDateFilterChange}><option value="default">Default</option><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="this_week">This Week</option><option value="this_month">This Month</option></select></Col>
            <Col lg="2" md="4"><select value={limitFilter} onChange={handleLimitChange}><option value="20">20</option><option value="50">50</option><option value="100">100</option><option value="200">200</option></select></Col>
            <div>
              <button className="save filter" onClick={addFilter}>Filter <i><BiFilter /></i></button>
              <button className="cancel reset" onClick={resetFilter}>Reset <i><GrPowerReset /></i></button>
            </div>
          </Row>
        </Row>

        {/* --- WALLETS TABLE --- */}
        <div id="directIncomeDiv">
          <Row id="directTableRow" className="text-center">
            <Col md="12" className="p-0">
              <div style={{ width: "100%", overflowX: "auto" }}>
                <table>
                    <tr>
                      <th>S No.</th>
                      <th>UID</th>
                      <th>Name</th>
                      <th>Username</th>
                      {dummyWallets?.data?.[0]?.wallets
                        .filter(wallet => !excludedWallets.includes(wallet.name))
                        .map((wallet) => (
                          <th key={wallet._id || wallet.slug}>{wallet.name}</th>
                      ))}
                      <th>Action</th>
                    </tr>
                  <tbody>
                    {dummyWallets?.data?.map((user, i) => (
                      <tr key={user._id}>
                        <td>{i + 1 + limitFilter * (currentPage - 1)}</td>
                        <td>{user.uid}</td>
                        <td>{user.name}</td>
                        <td>{user.username}</td>
                        {user.wallets
                          .filter(wallet => !excludedWallets.includes(wallet.name))
                          .map((wallet) => (
                            <td key={wallet._id || wallet.slug}>{wallet.value ?? 0}</td>
                        ))}
                        <td>
                          <Button variant="info" size="sm" onClick={() => handleUpdateModalShow(user)}>
                            Update
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </div>

        {/* --- PAGINATION --- */}
        <Row>
          <Col md="12">
            {totalPages > 1 && (
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePagination}
              />
            )}
          </Col>
        </Row>
      </Container>

      {/* --- UPDATE WALLETS MODAL --- */}
      <Modal show={showUpdateModal} onHide={handleUpdateModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Wallets</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUserForEdit && (
            <form onSubmit={handleUpdateSubmit}>
              <p><strong>Name:</strong> {selectedUserForEdit.name}</p>
              <p><strong>Username:</strong> {selectedUserForEdit.username}</p>
              <p><strong>UID:</strong> {selectedUserForEdit.uid}</p>
              <hr />
              {selectedUserForEdit.wallets
                .filter(wallet => !excludedWallets.includes(wallet.name))
                .map(wallet => (
                  <Row key={wallet.slug} className="mb-3 align-items-center">
                    <Col sm={4}>
                      <label htmlFor={wallet.slug} className="col-form-label">{wallet.name}</label>
                    </Col>
                    <Col sm={8}>
                      <input
                        type="number"
                        id={wallet.slug}
                        className="form-control"
                        value={editableWallets[wallet.slug] ?? ''}
                        onChange={(e) => handleWalletValueChange(wallet.slug, e.target.value)}
                      />
                    </Col>
                  </Row>
              ))}
              <div className="d-flex justify-content-end mt-4">
                <Button variant="secondary" onClick={handleUpdateModalClose} className="me-2">
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isUpdating}>
                  {isUpdating ? 'Updating...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default DummyUserWallet;
